import { graphql } from "@/lib/generated"

export const CreateSourceMutation = graphql(`
  mutation CreateSource($input: CreateSourceInput!) {
    createSource(input: $input) {
      id
      name
      slug
      providerType
      verifyConfig
      status
      rateLimitRps
      spikeProtection
      maxIngestRps
      brokerConfig
      responseConfig {
        statusCode
        body
        contentType
      }
      dedupConfig {
        strategy
        fields
        window
      }
      createdAt
    }
  }
`)

export const UpdateSourceMutation = graphql(`
  mutation UpdateSource($id: UUID!, $input: UpdateSourceInput!) {
    updateSource(id: $id, input: $input) {
      id
      name
      slug
      providerType
      verifyConfig
      status
      rateLimitRps
      spikeProtection
      maxIngestRps
      brokerConfig
      responseConfig {
        statusCode
        body
        contentType
      }
      dedupConfig {
        strategy
        fields
        window
      }
      createdAt
    }
  }
`)

export const DeleteSourceMutation = graphql(`
  mutation DeleteSource($id: UUID!) {
    deleteSource(id: $id)
  }
`)

export const CreateDestinationMutation = graphql(`
  mutation CreateDestination($input: CreateDestinationInput!) {
    createDestination(input: $input) {
      id
      name
      url
      type
      typeConfig
      deliveryMode
      pollApiKey
      status
      timeoutMs
      rateLimitRps
      retryPolicy {
        maxAttempts
        initialDelay
        maxDelay
        backoffFactor
      }
      headers
      authType
      ordered
      outputFormat
      healthScore
      disabledReason
      healthConfig {
        windowHours
        disableBelow
      }
      connectorType
      autoDeletePayload
      createdAt
    }
  }
`)

export const UpdateDestinationMutation = graphql(`
  mutation UpdateDestination($id: UUID!, $input: UpdateDestinationInput!) {
    updateDestination(id: $id, input: $input) {
      id
      name
      url
      type
      typeConfig
      deliveryMode
      pollApiKey
      status
      timeoutMs
      rateLimitRps
      retryPolicy {
        maxAttempts
        initialDelay
        maxDelay
        backoffFactor
      }
      headers
      authType
      ordered
      outputFormat
      healthScore
      disabledReason
      healthConfig {
        windowHours
        disableBelow
      }
      connectorType
      autoDeletePayload
      createdAt
    }
  }
`)

export const DeleteDestinationMutation = graphql(`
  mutation DeleteDestination($id: UUID!) {
    deleteDestination(id: $id)
  }
`)

export const RotateDestinationSecretMutation = graphql(`
  mutation RotateDestinationSecret($id: UUID!) {
    rotateDestinationSecret(id: $id) {
      id
      signingSecret
    }
  }
`)

export const CreateSubscriptionMutation = graphql(`
  mutation CreateSubscription($input: CreateSubscriptionInput!) {
    createSubscription(input: $input) {
      id
      name
      sourceId
      destinationId
      filterConfig {
        eventTypes
        regex
        bodyMatch {
          path
          value
          operator
        }
        rules {
          path
          operator
          value
          rules {
            path
            operator
            value
            rules {
              path
              operator
              value
            }
          }
        }
      }
      transformConfig {
        envelope
        headers
      }
      transformationId
      transformation {
        id
        name
      }
      enabled
      delay
      channels
      createdAt
    }
  }
`)

export const UpdateSubscriptionMutation = graphql(`
  mutation UpdateSubscription($id: UUID!, $input: UpdateSubscriptionInput!) {
    updateSubscription(id: $id, input: $input) {
      id
      name
      sourceId
      destinationId
      filterConfig {
        eventTypes
        regex
        bodyMatch {
          path
          value
          operator
        }
        rules {
          path
          operator
          value
          rules {
            path
            operator
            value
            rules {
              path
              operator
              value
            }
          }
        }
      }
      transformConfig {
        envelope
        headers
      }
      transformationId
      transformation {
        id
        name
      }
      enabled
      delay
      channels
      createdAt
    }
  }
`)

export const DeleteSubscriptionMutation = graphql(`
  mutation DeleteSubscription($id: UUID!) {
    deleteSubscription(id: $id)
  }
`)

export const CreateApiKeyMutation = graphql(`
  mutation CreateApiKey($input: CreateAPIKeyInput!) {
    createAPIKey(input: $input) {
      apiKey {
        id
        name
        keyPrefix
        scopes
        sourceIds
        createdAt
        expiresAt
      }
      rawKey
    }
  }
`)

export const RevokeApiKeyMutation = graphql(`
  mutation RevokeApiKey($id: UUID!) {
    revokeAPIKey(id: $id)
  }
`)

export const RotateSourceSecretMutation = graphql(`
  mutation RotateSourceSecret($id: UUID!) {
    rotateSourceSecret(id: $id) {
      id
      verifyConfig
    }
  }
`)

export const ClearSourceSecondarySecretMutation = graphql(`
  mutation ClearSourceSecondarySecret($id: UUID!) {
    clearSourceSecondarySecret(id: $id) {
      id
      verifyConfig
    }
  }
`)

export const CreateAlertRuleMutation = graphql(`
  mutation CreateAlertRule($input: CreateAlertRuleInput!) {
    createAlertRule(input: $input) {
      id
      name
      conditionType
      threshold
      webhookUrl
      channel
      emailConfig {
        to
        subjectTemplate
      }
      slackConfig {
        webhookUrl
        channel
      }
      cooldown
      enabled
      createdAt
    }
  }
`)

export const UpdateAlertRuleMutation = graphql(`
  mutation UpdateAlertRule($id: UUID!, $input: UpdateAlertRuleInput!) {
    updateAlertRule(id: $id, input: $input) {
      id
      name
      conditionType
      threshold
      webhookUrl
      channel
      emailConfig {
        to
        subjectTemplate
      }
      slackConfig {
        webhookUrl
        channel
      }
      cooldown
      enabled
      createdAt
    }
  }
`)

export const DeleteAlertRuleMutation = graphql(`
  mutation DeleteAlertRule($id: UUID!) {
    deleteAlertRule(id: $id)
  }
`)

export const TestAlertRuleMutation = graphql(`
  mutation TestAlertRule($id: UUID!) {
    testAlertRule(id: $id)
  }
`)

export const CreateEventTypeSchemaMutation = graphql(`
  mutation CreateEventTypeSchema($input: CreateEventTypeSchemaInput!) {
    createEventTypeSchema(input: $input) {
      id
      eventType
      description
      schema
      example
      createdAt
      updatedAt
    }
  }
`)

export const UpdateEventTypeSchemaMutation = graphql(`
  mutation UpdateEventTypeSchema($id: UUID!, $input: UpdateEventTypeSchemaInput!) {
    updateEventTypeSchema(id: $id, input: $input) {
      id
      eventType
      description
      schema
      example
      createdAt
      updatedAt
    }
  }
`)

export const DeleteEventTypeSchemaMutation = graphql(`
  mutation DeleteEventTypeSchema($id: UUID!) {
    deleteEventTypeSchema(id: $id)
  }
`)

export const CreateBookmarkMutation = graphql(`
  mutation CreateBookmark($eventId: UUID!, $name: String, $notes: String) {
    createBookmark(eventId: $eventId, name: $name, notes: $notes) {
      id
      eventId
      name
      notes
      createdAt
    }
  }
`)

export const DeleteBookmarkMutation = graphql(`
  mutation DeleteBookmark($id: UUID!) {
    deleteBookmark(id: $id)
  }
`)

export const CreateTransformationMutation = graphql(`
  mutation CreateTransformation($input: CreateTransformationInput!) {
    createTransformation(input: $input) {
      id
      name
      description
      code
      enabled
      failOpen
      timeoutMs
      envVars
      createdAt
      updatedAt
    }
  }
`)

export const UpdateTransformationMutation = graphql(`
  mutation UpdateTransformation($id: UUID!, $input: UpdateTransformationInput!) {
    updateTransformation(id: $id, input: $input) {
      id
      name
      description
      code
      enabled
      failOpen
      timeoutMs
      envVars
      createdAt
      updatedAt
    }
  }
`)

export const DeleteTransformationMutation = graphql(`
  mutation DeleteTransformation($id: UUID!) {
    deleteTransformation(id: $id)
  }
`)

export const TestTransformationMutation = graphql(`
  mutation TestTransformation($input: TestTransformationInput!) {
    testTransformation(input: $input) {
      success
      output
      error
      durationMs
    }
  }
`)

export const CreatePortalLinkMutation = graphql(`
  mutation CreatePortalLink($scope: PortalLinkScopeInput!, $expiresIn: String) {
    createPortalLink(scope: $scope, expiresIn: $expiresIn) {
      id
      token
      scope {
        sourceId
        eventId
        destinationId
        applicationId
      }
      expiresAt
      createdAt
    }
  }
`)

export const DeletePortalLinkMutation = graphql(`
  mutation DeletePortalLink($id: UUID!) {
    deletePortalLink(id: $id)
  }
`)

export const ReplayEventMutation = graphql(`
  mutation ReplayEvent($eventId: UUID!) {
    replayEvent(eventId: $eventId) {
      deliveries
    }
  }
`)

export const ReplayBySourceMutation = graphql(`
  mutation ReplayBySource($sourceId: UUID!, $since: String!) {
    replayBySource(sourceId: $sourceId, since: $since) {
      deliveries
    }
  }
`)

export const ReplayDLQEntryMutation = graphql(`
  mutation ReplayDLQEntry($id: UUID!) {
    replayDLQEntry(id: $id)
  }
`)

export const ReplayAllDLQMutation = graphql(`
  mutation ReplayAllDLQ {
    replayAllDLQ {
      deliveries
    }
  }
`)

export const PurgeDLQMutation = graphql(`
  mutation PurgeDLQ($olderThan: String!) {
    purgeDLQ(olderThan: $olderThan) {
      purged
    }
  }
`)

export const AckDeliveriesMutation = graphql(`
  mutation AckDeliveries($destinationId: UUID!, $deliveryIds: [UUID!]!) {
    ackDeliveries(destinationId: $destinationId, deliveryIds: $deliveryIds)
  }
`)

export const RegeneratePollApiKeyMutation = graphql(`
  mutation RegeneratePollApiKey($destinationId: UUID!) {
    regeneratePollApiKey(destinationId: $destinationId) {
      id
      deliveryMode
      pollApiKeyPrefix
      pollApiKey
    }
  }
`)

export const SkipDLQEntryMutation = graphql(`
  mutation SkipDLQEntry($id: UUID!) {
    skipDLQEntry(id: $id)
  }
`)

export const CreateOrganizationMutation = graphql(`
  mutation CreateOrganization($input: CreateOrganizationInput!) {
    createOrganization(input: $input) {
      id
      name
      slug
    }
  }
`)

export const UpdateOrganizationMutation = graphql(`
  mutation UpdateOrganization($id: UUID!, $input: UpdateOrganizationInput!) {
    updateOrganization(id: $id, input: $input) {
      id
      name
      slug
    }
  }
`)

export const DeleteOrganizationMutation = graphql(`
  mutation DeleteOrganization($id: UUID!) {
    deleteOrganization(id: $id)
  }
`)

export const ConfigureSsoMutation = graphql(`
  mutation ConfigureSSO($organizationId: UUID!, $input: SSOConfigInput!) {
    configureSSO(organizationId: $organizationId, input: $input) {
      id
      ssoEnabled
      ssoProvider
    }
  }
`)

export const DisableSsoMutation = graphql(`
  mutation DisableSSO($organizationId: UUID!) {
    disableSSO(organizationId: $organizationId) {
      id
      ssoEnabled
      ssoProvider
    }
  }
`)

export const InviteUserMutation = graphql(`
  mutation InviteUser($organizationId: UUID!, $input: InviteUserInput!) {
    inviteUser(organizationId: $organizationId, input: $input) {
      id
      email
      name
      role
    }
  }
`)

export const RemoveUserMutation = graphql(`
  mutation RemoveUser($id: UUID!) {
    removeUser(id: $id)
  }
`)

export const UpdateUserRoleMutation = graphql(`
  mutation UpdateUserRole($id: UUID!, $input: UpdateUserRoleInput!) {
    updateUserRole(id: $id, input: $input) {
      id
      role
    }
  }
`)

export const UpdateOrganizationRetentionMutation = graphql(`
  mutation UpdateOrganizationRetention($organizationId: UUID!, $input: RetentionInput!) {
    updateOrganizationRetention(organizationId: $organizationId, input: $input) {
      id
      retentionEvents
      retentionMessages
    }
  }
`)

export const ExportOrganizationDataMutation = graphql(`
  mutation ExportOrganizationData($organizationId: UUID!) {
    exportOrganizationData(organizationId: $organizationId)
  }
`)

export const DeleteOrganizationDataMutation = graphql(`
  mutation DeleteOrganizationData($organizationId: UUID!) {
    deleteOrganizationData(organizationId: $organizationId)
  }
`)

export const ConfigureOtlpMutation = graphql(`
  mutation ConfigureOTLP($organizationId: UUID!, $input: OTLPConfigInput!) {
    configureOTLP(organizationId: $organizationId, input: $input) {
      id
      otlpConfig {
        endpoint
        headers
        insecure
        sampleRate
      }
    }
  }
`)

export const DisableOtlpMutation = graphql(`
  mutation DisableOTLP($organizationId: UUID!) {
    disableOTLP(organizationId: $organizationId) {
      id
      otlpConfig {
        endpoint
        headers
        insecure
        sampleRate
      }
    }
  }
`)

export const ConfigureMetricsExportMutation = graphql(`
  mutation ConfigureMetricsExport($organizationId: UUID!, $input: MetricsExportConfigInput!) {
    configureMetricsExport(organizationId: $organizationId, input: $input) {
      id
      metricsExportConfig {
        type
        apiKey
        endpoint
        interval
      }
    }
  }
`)

export const DisableMetricsExportMutation = graphql(`
  mutation DisableMetricsExport($organizationId: UUID!) {
    disableMetricsExport(organizationId: $organizationId) {
      id
      metricsExportConfig {
        type
        apiKey
        endpoint
        interval
      }
    }
  }
`)

export const TestWebhookVerificationMutation = graphql(`
  mutation TestWebhookVerification($input: TestWebhookVerificationInput!) {
    testWebhookVerification(input: $input) {
      success
      error
      eventType
      idempotencyKey
    }
  }
`)

export const ImportEventTypesFromOpenAPIMutation = graphql(`
  mutation ImportEventTypesFromOpenAPI($spec: String!) {
    importEventTypesFromOpenAPI(spec: $spec) {
      id
      eventType
      description
      schema
      example
      createdAt
      updatedAt
    }
  }
`)

export const ForceRetryDeliveryMutation = graphql(`
  mutation ForceRetryDelivery($id: UUID!) {
    forceRetryDelivery(id: $id) {
      id
      status
      attempts
    }
  }
`)

export const BulkRetryDeliveriesMutation = graphql(`
  mutation BulkRetryDeliveries($filter: BulkRetryFilterInput!) {
    bulkRetryDeliveries(filter: $filter) {
      count
    }
  }
`)

export const CreateMetaEventConfigMutation = graphql(`
  mutation CreateMetaEventConfig($input: CreateMetaEventConfigInput!) {
    createMetaEventConfig(input: $input) {
      id
      name
      url
      signingSecret
      eventTypes
      enabled
      createdAt
    }
  }
`)

export const UpdateMetaEventConfigMutation = graphql(`
  mutation UpdateMetaEventConfig($id: UUID!, $input: UpdateMetaEventConfigInput!) {
    updateMetaEventConfig(id: $id, input: $input) {
      id
      name
      url
      signingSecret
      eventTypes
      enabled
      createdAt
    }
  }
`)

export const DeleteMetaEventConfigMutation = graphql(`
  mutation DeleteMetaEventConfig($id: UUID!) {
    deleteMetaEventConfig(id: $id)
  }
`)

export const BulkDeleteSourcesMutation = graphql(`
  mutation BulkDeleteSources($ids: [UUID!]!) {
    bulkDeleteSources(ids: $ids)
  }
`)

export const BulkDeleteDestinationsMutation = graphql(`
  mutation BulkDeleteDestinations($ids: [UUID!]!) {
    bulkDeleteDestinations(ids: $ids)
  }
`)

export const BulkDeleteSubscriptionsMutation = graphql(`
  mutation BulkDeleteSubscriptions($ids: [UUID!]!) {
    bulkDeleteSubscriptions(ids: $ids)
  }
`)

export const BulkDeleteEventsMutation = graphql(`
  mutation BulkDeleteEvents($ids: [UUID!]!) {
    bulkDeleteEvents(ids: $ids)
  }
`)
